<template>
  <f7-page
    :page-content="true"
    name="normasvacaciones"
    class="pagina-normas-vacaciones"
    @page:beforein="entrar"
    @page:beforeout="salir"
  >
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>{{ $t('normasvacaciones.titulo') }}</f7-nav-title>
    </f7-navbar>

    <f7-block v-if="error403" strong>
      <p>{{ $t('normasvacaciones.noAutorizado') }}</p>
    </f7-block>

    <template v-else>
      <f7-block strong class="no-margin-bottom">
        <p class="explicacion">
          {{ $t('normasvacaciones.explicacion') }}
          <!-- El «i» de la pantalla. Lo pidió dirección el 02/10/2026 al aceptar por escrito el
               riesgo de publicar con la conexión cortada: va dicho en tres sitios —aquí, en la
               incidencia #56 y en el README del lote de despliegue— y no en silencio. Es texto
               fijo de la pantalla, no un concepto de los del dashboard: esos viven en Mongo por
               clave y pedirían sembrarlos con un guion más, para un texto que no se edita. -->
          <!-- Botón de verdad, y no un `span` anunciado como botón por su atributo `role`: aquello
               decía ser un botón a quien lee la pantalla, pero no se podía alcanzar con el
               tabulador ni abrir con Intro o Espacio, así que por teclado el «i» no existía. El
               foco y las dos teclas los pone el navegador en un `<button>`; no hay nada que
               implementar. `type="button"` porque el tipo por defecto de un botón es `submit`: sin
               él, el día que esto caiga dentro de un formulario, abrir la ayuda lo enviaría. -->
          <button
            type="button"
            class="icono-info"
            :aria-label="$t('normasvacaciones.infoAria')"
            :title="$t('normasvacaciones.infoAria')"
            @click="infoAbierta = true"
          >i</button>
        </p>
      </f7-block>

      <f7-block class="no-margin-bottom">
        <!-- Mientras se carga no se dice todavía ni la versión ni «no hay ninguna»: hasta que
             conteste el servidor no se sabe cuál de las dos cosas es. -->
        <span v-if="cargando" class="etiqueta-version etiqueta-sin-version">
          {{ $t('normasvacaciones.cargando') }}
        </span>
        <span v-else-if="version" class="etiqueta-version">
          {{ $t('normasvacaciones.version') }} {{ version }}
          · {{ fechaPublicacion | dateFormat('DD/MM/YYYY') }}
        </span>
        <!-- Antes de lanzar el script de carga de la versión 1 no hay ninguna, y eso no es un
             fallo de carga: decirlo evita que quien entre crea que la pantalla no funciona. -->
        <span v-else-if="!cargaFallida" class="etiqueta-version etiqueta-sin-version">
          {{ $t('normasvacaciones.sinVersion') }}
        </span>
        <span v-else class="etiqueta-version etiqueta-sin-version">
          {{ $t('normasvacaciones.errorCarga') }}
          <f7-link class="margin-left-half" @click="cargar()">{{ $t('normasvacaciones.reintentar') }}</f7-link>
        </span>
      </f7-block>

      <!-- Otra persona publicó mientras se editaba. El texto del área no incluye su cambio, así
           que publicarlo tal cual lo dejaría deshecho sin posibilidad de recuperarlo (I6). Hasta
           que se diga qué hacer con el borrador no se publica: avanzar la base sola convertiría
           el 409 en un retroceso silencioso, que es justo lo que la base vigilaba. -->
      <f7-block v-if="conflicto" strong class="aviso-conflicto">
        <p>{{ textoConflicto }}</p>
        <div class="acciones-conflicto">
          <f7-link v-if="conflicto.version" @click="ver(conflicto.version)">
            {{ $t('normasvacaciones.conflictoVer', { version: conflicto.version }) }}
          </f7-link>
          <f7-link @click="descartarBorrador()">{{ $t('normasvacaciones.conflictoDescartar') }}</f7-link>
          <f7-link v-if="conflicto.version" @click="asumirConflicto()">
            {{ $t('normasvacaciones.conflictoAsumir', { version: conflicto.version }) }}
          </f7-link>
        </div>
      </f7-block>

      <!-- La publicación se mandó y no se sabe en qué quedó: su respuesta no llegó y la relectura
           de qué hay en vigor tampoco ha podido decirlo. Mientras eso dure, **el intento es una
           instantánea inmutable**: el texto y el motivo que se mandaron se conservan tal cual, no
           se pueden editar y no se puede lanzar otra publicación. Lo que se ofrece es volver a
           comprobar —o descartar el borrador, que es una decisión explícita—.

           Es la opción 1 que eligió dirección el 01/10/2026. El motivo: el problema no era quién
           deducía el desenlace, sino que mientras la pantalla lo deducía había dos manos sobre el
           mismo texto, así que al resolverse se perdía lo escrito en ese rato. Con el intento
           congelado no hay nada que reconciliar: lo que se compara con lo que haya en vigor es
           exactamente lo que se mandó. -->
      <f7-block
        v-if="desenlaceDesconocido && !publicando && !cargando"
        strong
        class="aviso-conflicto"
      >
        <p>{{ textoDesenlaceDesconocido }}</p>
        <div v-if="comprobando" class="acciones-conflicto">
          {{ $t('normasvacaciones.comprobando') }}
        </div>
        <div v-else class="acciones-conflicto">
          <f7-link @click="reintentarComprobacion()">
            {{ $t('normasvacaciones.comprobarOtraVez') }}
          </f7-link>
          <!-- Reenviar EL MISMO intento: con su identidad, el backend reconoce la escritura que
               pudiera haber entrado y devuelve esa versión en vez de publicar otra, así que esto no
               puede acabar en dos versiones del mismo texto (I6). Solo aparece cuando el POST
               terminó de forma ambigua: si el servidor dijo que no escribió, lo que toca es el
               botón de siempre. -->
          <f7-link v-if="sinConfirmar" @click="reintentarPublicacion()">
            {{ $t('normasvacaciones.publicarOtraVez') }}
          </f7-link>
          <f7-link @click="descartarBorrador()">
            {{ $t('normasvacaciones.conflictoDescartar') }}
          </f7-link>
        </div>
      </f7-block>

      <!-- Deshabilitados en tres casos, y los tres por el mismo motivo —que no haya dos manos
           sobre el mismo texto—: mientras se publica, mientras se carga (la carga acaba en
           `aplicarVigente`, que pisa el área con lo que venga del servidor) y mientras una
           publicación tenga desenlace desconocido, que incluye la relectura que lo averigua. -->
      <f7-list no-hairlines-md class="no-margin-top">
        <f7-list-input
          :label="$t('normasvacaciones.normas')"
          type="textarea"
          resizable
          class="area-normas"
          :value="texto"
          :disabled="camposBloqueados"
          @input="texto = $event.target.value"
        ></f7-list-input>
        <f7-list-input
          :label="$t('normasvacaciones.motivo')"
          type="text"
          :placeholder="$t('normasvacaciones.motivoPlaceholder')"
          :value="motivo"
          :disabled="camposBloqueados"
          :error-message="$t('normasvacaciones.faltaMotivo')"
          :error-message-force="faltaMotivo"
          @input="cambiarMotivo($event.target.value)"
        ></f7-list-input>
      </f7-list>

      <f7-block>
        <f7-button
          fill
          :disabled="camposBloqueados || !!conflicto"
          @click="publicar()"
        >
          {{ $t('normasvacaciones.publicar') }}
        </f7-button>
      </f7-block>

      <f7-block-title>{{ $t('normasvacaciones.historial') }}</f7-block-title>
      <div class="tabla-versiones">
        <div class="fila cabecera">
          <span class="col-version">{{ $t('normasvacaciones.colVersion') }}</span>
          <span class="col-fecha">{{ $t('normasvacaciones.colFecha') }}</span>
          <span class="col-autor">{{ $t('normasvacaciones.colAutor') }}</span>
          <span class="col-motivo">{{ $t('normasvacaciones.colMotivo') }}</span>
          <span class="col-ver"></span>
        </div>
        <div v-for="v in historial" :key="v.version" class="fila">
          <span class="col-version">v{{ v.version }}</span>
          <span class="col-fecha">{{ v.fechaPublicacion | dateFormat('DD/MM/YYYY') }}</span>
          <span class="col-autor">{{ v.autor }}</span>
          <span class="col-motivo">{{ v.motivo }}</span>
          <span class="col-ver">
            <f7-link @click="ver(v.version)">{{ $t('normasvacaciones.ver') }}</f7-link>
          </span>
        </div>
        <!-- «Sin versiones publicadas» es una AFIRMACIÓN, así que solo se dice cuando la tabla se
             ha leído de verdad: mientras carga no se sabe, y si la lectura falló tampoco. Antes
             salía con el historial a `[]` de partida, así que en la primera entrada se enseñaba
             junto al aviso de error —y junto a una etiqueta que podía decir «Versión 5»—, que en
             una pantalla de trazabilidad es dar por hecho lo que no se ha podido consultar. -->
        <div v-if="historialCargado && !historialCargando && !historial.length" class="fila vacia">
          {{ $t('normasvacaciones.sinVersiones') }}
        </div>
        <!-- Mientras se relee, la tabla de delante es la de ANTES de la relectura, y la etiqueta de
             arriba ya puede haber avanzado —al publicar, `aplicarVigente` pasa a «Versión 2» y el
             historial se pide detrás—. Dicho de otra forma: lo que se enseña aquí deja de cuadrar
             con lo de arriba justo en el rato en que la petición viaja, así que se dice que está
             actualizándose en vez de dejar una tabla que termina en v1 al lado de «Versión 2» sin
             un solo aviso (revisión CODEX del PR #1725). Es el mismo criterio que la etiqueta, que
             ya decía «Cargando…» durante su carga. -->
        <div v-if="historialCargando" class="fila aviso-historial">
          {{ $t('normasvacaciones.actualizandoHistorial') }}
        </div>
        <!-- El historial se relee aparte de la versión vigente, así que puede quedarse atrás él
             solo: se dice y se deja reintentar, en vez de vaciar la tabla o de dar por fallido lo
             de arriba, que está al día.
             Con una relectura en vuelo no se enseña: el aviso describe una lectura que ya ha dejado
             de ser la última, y su enlace ofrecería reintentar lo que se está haciendo. -->
        <div v-if="historialDesfasado && !historialCargando" class="fila aviso-historial">
          {{ $t('normasvacaciones.errorHistorial') }}
          <f7-link class="margin-left-half" @click="historialAlDia()">
            {{ $t('normasvacaciones.reintentar') }}
          </f7-link>
        </div>
      </div>

      <!-- #271 · El interruptor del camino manual. Es suyo entero —carga, errores y accionamiento—:
           de esta pantalla solo toma el sitio y el aviso de que se entra o se sale. -->
      <interruptor-camino-manual ref="caminoManual"></interruptor-camino-manual>

      <f7-block class="pie">
        <p>{{ $t('normasvacaciones.pie') }}</p>
      </f7-block>
    </template>

    <!-- «Ver» de una fila del historial: el texto de esa versión, solo lectura. Entra por la
         decisión de dirección del 22/09/2026: hay que poder enseñar qué normas aceptó una
         persona en una fecha concreta (trazabilidad 17020). -->
    <f7-popup :opened="verAbierto" @popup:closed="cerrarVer()">
      <f7-page>
        <f7-navbar :title="tituloVersionVista">
          <f7-nav-right>
            <f7-link popup-close>{{ $t('cerrar') }}</f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-block v-if="versionVista" strong>
          <!-- Numeradas aquí y no en el dato: el número es la posición de la línea, así que se
               pinta al leer. Guardarlo dentro del texto obligaría a renumerar al insertar. -->
          <ol class="lista-normas-vista">
            <li v-for="(n, i) in versionVista.normas" :key="i">{{ n }}</li>
          </ol>
        </f7-block>
        <f7-block v-else strong>
          <p>{{ $t('normasvacaciones.cargandoVersion') }}</p>
        </f7-block>
      </f7-page>
    </f7-popup>

    <!-- Lo que hay que saber de esta pantalla, y en particular lo que dirección asumió por escrito
         el 02/10/2026. Se abre desde el «i» de arriba. -->
    <f7-popup :opened="infoAbierta" @popup:closed="infoAbierta = false">
      <f7-page>
        <f7-navbar :title="$t('normasvacaciones.infoTitulo')">
          <f7-nav-right>
            <f7-link popup-close>{{ $t('cerrar') }}</f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-block strong class="bloque-info">
          <h4>{{ $t('normasvacaciones.infoVersionesTitulo') }}</h4>
          <p>{{ $t('normasvacaciones.infoVersionesTexto') }}</p>

          <h4>{{ $t('normasvacaciones.infoRedTitulo') }}</h4>
          <p>{{ $t('normasvacaciones.infoRedTexto') }}</p>
          <p>{{ $t('normasvacaciones.infoRedNunca') }}</p>
          <p class="matiz">{{ $t('normasvacaciones.infoRedPorQue') }}</p>
        </f7-block>
      </f7-page>
    </f7-popup>
  </f7-page>
</template>

<style scoped>
.explicacion { font-size: 13px; color: #475569; margin: 0; }
/* Mismo aspecto que el «i» de los dashboards (`InfoIcon.vue`), sin su maquinaria: aquí el texto
   es fijo y no hay concepto que leer de Mongo. Es un `<button>`, así que lo primero es quitarle
   el borde, el relleno y el aspecto que le pone el navegador: lo que se ve tiene que ser el mismo
   círculo de antes. */
.icono-info {
  width: 14px; height: 14px; border-radius: 50%;
  border: 0; padding: 0; line-height: 1;
  -webkit-appearance: none; appearance: none;
  color: #fff; font-weight: bold; font-style: italic; font-family: Georgia, serif; font-size: 10px;
  display: inline-flex; align-items: center; justify-content: center;
  background-color: #22355b;
  cursor: pointer; margin-left: 4px; vertical-align: middle;
  user-select: none;
}
/* Quien llega con el tabulador tiene que VER dónde está. El `:focus` a secas es el que funciona
   en todas partes; el par de abajo lo retira al pulsar con el ratón donde el navegador sabe
   distinguirlo. Un navegador que no entienda `:focus-visible` descarta las dos reglas y se queda
   con el recuadro siempre, que es el fallo del lado seguro. */
.icono-info:focus { outline: 2px solid #22355b; outline-offset: 2px; }
.icono-info:focus:not(:focus-visible) { outline: none; }
.icono-info:focus-visible { outline: 2px solid #22355b; outline-offset: 2px; }
.bloque-info h4 { font-size: 14px; margin: 16px 0 6px; }
.bloque-info h4:first-child { margin-top: 0; }
.bloque-info p { font-size: 13px; line-height: 1.5; margin: 0 0 8px; }
.bloque-info .matiz { color: #64748b; }
.etiqueta-version {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: bold;
  color: #fff;
  background-color: #22355b;
}
.etiqueta-sin-version { background-color: #8e8e93; }
.aviso-conflicto { font-size: 13px; color: #92400e; background-color: #fef3c7; }
.aviso-conflicto p { margin: 0 0 8px; }
.acciones-conflicto { display: flex; flex-wrap: wrap; }
.acciones-conflicto .link { margin-right: 16px; }
.tabla-versiones { font-size: 13px; padding: 0 10px; }
.tabla-versiones .fila {
  display: flex;
  align-items: baseline;
  padding: 6px 8px;
  border-bottom: 1px solid #f0f0f0;
}
.tabla-versiones .cabecera {
  font-weight: bold;
  color: #64748b;
  text-transform: uppercase;
  font-size: 11px;
}
.tabla-versiones .vacia { color: #8e8e93; }
.tabla-versiones .aviso-historial { color: #92400e; background-color: #fef3c7; }
.col-version { width: 60px; }
.col-fecha { width: 100px; }
.col-autor { width: 140px; }
.col-motivo { flex: 1 1 auto; }
.col-ver { width: 60px; text-align: right; }
.pie { font-size: 12px; color: #64748b; }
.lista-normas-vista { font-size: 13px; padding-left: 20px; }
.lista-normas-vista li { margin-bottom: 6px; }
</style>

<!-- Sin `scoped` y con el nombre de la pantalla delante, patrón de PropiedadesTablaPopup: el
     `textarea` lo pinta f7-list-input por dentro, así que una regla scoped no lo alcanza. El
     prefijo es lo que impide que el alto se lleve por delante los textarea de otras pantallas. -->
<style>
.pagina-normas-vacaciones .area-normas textarea { min-height: 260px; font-size: 13px; }
</style>

<script>
import RhNormasVacaciones from "./../../js/db/RhNormasVacaciones.js";
import InterruptorCaminoManual from "./interruptor-camino-manual.vue";
import carrilPeticiones from "./../../js/utils/carrilPeticiones.js";
import mensajeDelServidor from "./../../js/utils/mensajeDelServidor.js";

export default {
  components: {
    'interruptor-camino-manual': InterruptorCaminoManual
  },
  data: function() {
    return {
      // Versión vigente que se tiene delante. Viaja al backend como `versionBase` al publicar:
      // es lo que permite rechazar la publicación si otra persona ha publicado entretanto.
      version: null,
      fechaPublicacion: null,
      texto: '',
      // El texto tal como vino, para saber si se ha cambiado algo. Una versión publicada no se
      // borra (I6), así que publicar una copia idéntica deja un documento que nadie quería.
      textoOriginal: '',
      motivo: '',
      // El aviso de «falta el motivo» solo se enseña cuando ya se ha intentado publicar: en
      // blanco al entrar es lo normal, no un error de quien acaba de abrir la pantalla.
      faltaMotivo: false,
      historial: [],
      // Si la tabla que se enseña viene de una lectura que salió bien. Es lo que separa «no hay
      // versiones publicadas» —una afirmación— de «todavía no lo sé»: en la primera entrada y
      // cuando la lectura falla, `historial` está a `[]` sin que nadie haya mirado.
      historialCargado: false,
      // El historial no se ha podido releer, pero lo de arriba sí: la tabla se queda como estaba
      // y se dice, en vez de vaciarla o de contar el fallo como fallo de la carga o de publicar.
      historialDesfasado: false,
      // Hay una lectura del historial en vuelo. Mientras dure, la tabla que se enseña es la de
      // antes de esa lectura, y la etiqueta de arriba ya puede haber avanzado: el historial se pide
      // DETRÁS de la vigente —desde su `exito`— y no bloquea nada, así que entre las dos hay
      // siempre un rato en que no cuadran. Lo que se enseña es que está actualizándose; sin esto,
      // `historialCargado` e `historialDesfasado` conservaban su valor anterior y la tabla afirmaba
      // con una lectura vieja algo que ya se estaba releyendo.
      historialCargando: false,
      publicando: false,
      // Hay una carga de la versión vigente en vuelo. Deshabilita el área, el motivo y el botón:
      // la carga termina en `aplicarVigente`, que pisa `texto`, así que lo que se escribiera
      // mientras llega la respuesta se perdería sin avisar.
      cargando: false,
      error403: false,
      // La carga falló: la etiqueta de versión no puede decir «sin versiones publicadas», que es
      // otra cosa distinta, y hace falta un sitio desde donde reintentar.
      cargaFallida: false,
      // Otra persona publicó mientras se editaba: la versión que hay ahora en vigor, apartada
      // SIN tocar `version` ni `texto`. Mientras esté puesto no se publica, porque el texto de
      // delante parte de una versión anterior a esta. `{ version, fechaPublicacion, texto }`.
      conflicto: null,
      // La publicación falló y la relectura de qué hay en vigor TAMBIÉN. No se sabe si la base que
      // se tiene delante sigue siendo la vigente, así que no se publica: reintentar mandaría el
      // mismo `versionBase` y chocaría igual. Se bloquea el botón y se ofrece otra comprobación,
      // sin tocar el área de texto. Antes la relectura no tenía `fallo`: el carril resolvía sin
      // ejecutar nada, el botón se volvía a habilitar con la base vieja y cada reintento chocaba
      // otra vez; la única salida era salir y volver a entrar, perdiendo el borrador
      // (revisión CODEX del PR #1725).
      baseSinComprobar: false,
      // Hay una relectura de «qué versión está en vigor» en vuelo, lanzada para averiguar en qué
      // quedó la publicación. El bloqueo de los campos dura TAMBIÉN durante ella: si no, se podría
      // escribir justo mientras se decide el desenlace y lo escrito se perdería al resolverse, que
      // es lo que se cerró con la opción 1 (dirección, 01/10/2026).
      comprobando: false,
      /**
       * La publicación que se ha mandado y de la que no se sabe el desenlace. Es **el único estado
       * que decide qué puede hacer la pantalla mientras tanto**, y tiene su nota de estados y
       * transiciones al lado, en `desenlace-publicacion.md`.
       *
       * `{ id, base, texto, motivo, enVuelo, confirmada, ambiguo }`:
       *
       * - `id` — la **identidad del intento**: una cadena que no se repite, que viaja en el POST y
       *   que el backend guarda en la versión que publique. Es lo que permite reconocer una
       *   publicación PROPIA que entró aunque su respuesta no llegase —un 504, una conexión cortada
       *   con `status` 0—. Antes se reconocía comparando el texto publicado con el enviado, y eso
       *   confunde dos cosas distintas: dos personas que publican el mismo texto con motivos
       *   distintos son indistinguibles así, de modo que quien recibía el 409 veía su publicación
       *   por hecha y se le limpiaba el motivo, que no quedaba en ningún sitio (reproducido por
       *   dirección el 01/10/2026).
       * - `base` — la versión que se tenía delante al mandar.
       * - `texto` y `motivo` — **la instantánea inmutable**: mientras el intento esté puesto, lo que
       *   se mandó es lo que hay delante y no se puede editar (`camposBloqueados`).
       * - `enVuelo` — el POST todavía no ha terminado. Mientras lo esté, **ninguna lectura cierra el
       *   intento** salvo que vea publicada su identidad: lo que haya en vigor ahora no dice en qué
       *   va a quedar una escritura que sigue viajando.
       * - `confirmada` — el POST contestó que sí, aunque su respuesta llegara fuera de turno. Es un
       *   desenlace que ya no hay que deducir de ninguna lectura.
       * - `ambiguo` — el POST terminó **sin que el servidor dijera que no había escrito**: un 504 de
       *   la pasarela, un `status` 0, un 500, o salir de la pantalla a medias. Entonces ninguna
       *   lectura puede concluir «no se publicó» —la escritura puede seguir en curso—, así que el
       *   intento se queda sin confirmar y lo que se ofrece es volver a comprobar o reenviarlo con la
       *   MISMA identidad. Sin esto, la relectura veía la base en su sitio, lo cerraba como
       *   `'intacta'` y desbloqueaba la edición (revisión CODEX del PR #1725).
       *
       * Está en `data` porque se pinta: lo que bloquea la pantalla es tener un intento sin desenlace.
       */
      intento: null,
      verAbierto: false,
      versionVista: null,
      numeroVersionVista: null,
      // El popup del «i». Texto fijo: no carga nada, así que no necesita carril ni descarte.
      infoAbierta: false
    };
  },
  created: function() {
    // Un carril para todo lo que escribe el estado de la versión vigente —entrar, reintentar,
    // publicar—, así que solo contesta la última (ver carrilPeticiones). Hace falta porque la ruta
    // tiene `keepAlive`: salir no destruye la pantalla, así que una petición lenta de antes sigue
    // viva y, sin descartarla, pisa lo que acaba de cargarse o vacía el área desde su `catch`
    // aunque la carga buena haya ido bien. Fuera de `data` porque no es estado que se pinte.
    //
    // «Ver» no va por aquí: tiene su propio descarte, porque lo que decide si su respuesta vale no
    // es solo ser la última, sino ser la de la apertura que está delante (ver `ver`).
    //
    // #472 · Y los dos avisos de lectura en marcha —`cargando` y `comprobando`— los apaga el
    // carril, no un `then` por lectura con su número de generación: la carga vieja que el carril
    // descarta no apaga nada, y una lectura descartada al tomar el carril (`invalidar`) deja de
    // contar en ese mismo momento, aunque su conexión se quede colgada (revisión CODEX del PR
    // #1725). Quien enciende cada aviso sigue siendo quien lanza su lectura: el carril no sabe si
    // lo que pide es una carga o una comprobación, pero cuando ya no queda ninguna que pueda
    // escribir, no puede haber ninguna de las dos en marcha. `publicando` no entra: no es una
    // lectura y lo suelta su propio `.then()`.
    const self = this;
    this.carril = carrilPeticiones({
      cargando: function(si) {
        if (si) return;
        self.cargando = false;
        self.comprobando = false;
      }
    });
    // Carril PROPIO para el historial, la tabla de abajo. Se relee desde la carga, desde la
    // publicación, desde la relectura del conflicto y desde su enlace de reintento, y de todas
    // solo vale la última. Va aparte porque su fallo es aparte: la versión vigente puede estar
    // perfectamente cargada —o recién publicada, que ya no se deshace— con la tabla sin releer.
    //
    // #472 · Su «Actualizando el historial…» es entero del carril: encendido al lanzar y apagado
    // cuando contesta la lectura vigente o cuando se toma el carril, nunca por una descartada.
    this.carrilHistorial = carrilPeticiones({
      cargando: function(si) { self.historialCargando = si; }
    });
    // Número de la última apertura de «Ver» que se ha lanzado. Va aparte del número de versión
    // porque dos aperturas seguidas de la MISMA fila lo comparten, y entonces la respuesta tardía
    // de la primera se colaba como si fuera de la segunda (revisión CODEX del PR #1725).
    this.ultimaVista = 0;
    // Si la pantalla está delante. La ruta tiene `keepAlive`, así que sobrevive a salir y volver y
    // hay que poder distinguir las dos cosas: lo que termina mientras no se está mirando no tiene
    // nada que arreglar —entrar vuelve a cargar—, y lo que termina estando delante sí.
    this.activa = false;
    // Cuántos intentos de publicación se han lanzado desde esta pantalla. Solo se usa para la
    // identidad de respaldo cuando no hay `crypto` (ver `nuevaIdentidadDeIntento`); dentro de una
    // pestaña no se repite, que es lo que el respaldo necesita garantizar.
    this.intentosLanzados = 0;
  },
  computed: {
    /**
     * Hay una publicación mandada de la que no se sabe el desenlace. Mientras dure, el intento es
     * una instantánea: no se edita ni se publica otra vez (opción 1, dirección 01/10/2026).
     *
     * Se apaga en cuanto el desenlace se sabe, sea cual sea —aplicada, intacta o ajena—, y de eso
     * se encarga `aplicarDesenlace`, que es el único sitio que lo decide.
     */
    desenlaceDesconocido: function() {
      return !!this.intento;
    },
    /**
     * El texto y el motivo no se tocan mientras haya alguien más tocándolos: la publicación en
     * curso, la carga que acaba pisando el área, o la instantánea de un intento sin desenlace.
     * En un solo sitio porque los tres campos —área, motivo y botón— tienen que bloquearse juntos:
     * repartido, el botón se quedaba habilitado en un caso en el que los campos no lo estaban.
     */
    camposBloqueados: function() {
      return this.publicando || this.cargando || this.desenlaceDesconocido;
    },
    /**
     * El intento terminó mal pero de una manera que no dice si escribió, y la comprobación todavía
     * no ve su identidad publicada. Es lo que habilita «Volver a publicar», que reenvía el mismo
     * intento: la única salida que no depende de que el servidor acabe contestando algo.
     */
    sinConfirmar: function() {
      return !!this.intento && !this.intento.enVuelo && !!this.intento.ambiguo;
    },
    /**
     * Por qué está bloqueada la pantalla, dicho con lo que se sabe. Tres cosas distintas, y en este
     * orden: si la relectura llegó a fallar se dice eso, que es lo más concreto; si el POST terminó
     * de forma ambigua se dice que pudo quedar publicado; y si todavía no se ha intentado nada —se
     * salió y se volvió con el intento pendiente—, que no se sabe en qué quedó.
     */
    textoDesenlaceDesconocido: function() {
      if (this.baseSinComprobar) return this.$t('normasvacaciones.baseSinComprobar');
      if (this.sinConfirmar) return this.$t('normasvacaciones.sinConfirmar');
      return this.$t('normasvacaciones.desenlaceDesconocido');
    },
    tituloVersionVista: function() {
      const numero = this.numeroVersionVista;
      return numero ? this.$t('normasvacaciones.version') + ' ' + numero : this.$t('normasvacaciones.ver');
    },
    textoConflicto: function() {
      if (!this.conflicto) return '';
      // Sin número no se puede ofrecer «publicar sobre ella» —no hay base que mandar— ni abrir su
      // texto, así que el aviso tiene que pedir otra cosa: recargar. No debería pasar, porque una
      // versión publicada no se borra (I6), pero si la relectura no ve ninguna no se puede
      // publicar a ciegas sobre lo que sea que haya.
      if (!this.conflicto.version) return this.$t('normasvacaciones.conflictoDesconocido');
      return this.$t('normasvacaciones.conflicto', { version: this.conflicto.version });
    }
  },
  methods: {
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    tratarError: function(e) {
      if (this.es403(e)) {
        this.error403 = true;
      } else {
        this.$f7.dialog.alert(mensajeDelServidor(e, this.$t('normasvacaciones.errorCarga')));
      }
    },
    /** Entrar en la pantalla: se apunta que está delante y se carga. */
    entrar: function() {
      this.activa = true;
      this.cargar();
      // #271 · la posición del interruptor puede haber cambiado desde la última visita.
      if (this.$refs.caminoManual) this.$refs.caminoManual.cargar();
    },
    /**
     * La ruta tiene `keepAlive`, así que la pantalla sobrevive a salir y volver. Se limpia desde
     * `@page:beforeout` y no desde `deactivated`: el keepAlive de Framework7 no monta ni
     * desmonta el componente, así que los ganchos de Vue no llegan a dispararse.
     *
     * Lo que se limpia es lo que no debe sobrevivir a una salida: el motivo del cambio y el aviso
     * de conflicto —los dos son de la publicación que se estaba preparando, no del estado de la
     * pantalla— y la ventana de «Ver». El texto no: se recarga al volver a entrar, y es esa
     * recarga la que vuelve a dejar cuadrados el texto y la versión de la que parte.
     *
     * <b>Con un intento sin desenlace no se limpia nada de eso</b>, y es la otra mitad de la
     * opción 1: el intento es una instantánea, así que el motivo forma parte de lo que se mandó y
     * se conserva igual que el texto. Salir y volver a entrar es justo la vía por la que una
     * recarga tardía podía descartar el borrador sin que nadie lo decidiera.
     *
     * Y se invalida la carga en curso: una respuesta que llega cuando ya se ha salido no tiene
     * pantalla donde pintar, y su `catch` abriría el diálogo de error encima de otra.
     */
    salir: function() {
      this.activa = false;
      if (this.$refs.caminoManual) this.$refs.caminoManual.soltar();
      this.tomarCarrilDeLaVigente();
      this.tomarCarrilDelHistorial();
      // Lo que NO se limpia es el intento: salir mientras el POST viaja es justo el caso en el que
      // hace falta al volver, porque es la carga de entonces la que tiene que reconocer si entró.
      // Se cierra solo, en `aplicarDesenlace`, en cuanto se sabe en qué quedó.
      if (!this.intento) {
        this.motivo = '';
        this.faltaMotivo = false;
        this.conflicto = null;
        // Como el conflicto: es de la publicación que se estaba preparando. Al volver a entrar se
        // carga de nuevo, y es esa carga la que vuelve a dejar cuadradas la base y el texto.
        this.baseSinComprobar = false;
      }
      this.cerrarVer();
    },
    /**
     * <b>Se toma el carril de la versión vigente</b>: lo que hubiera en vuelo queda descartado y,
     * con ello, los avisos que dicen que hay una lectura en marcha.
     *
     * Esta es la forma del fallo y no un sitio concreto: la promesa de una lectura descartada no se
     * resuelve hasta que su petición termine —y una conexión colgada no termina nunca—, así que un
     * aviso que esperase a esa promesa se quedaba puesto esperando a una lectura que ya no cuenta.
     * Desde la #472 los suelta el propio carril al invalidar (su opción `cargando`, ver `created`).
     *
     * Con `cargando` no se llegaba a ver, porque volver a entrar llama a `cargar()` y es esa carga
     * nueva la que lo suelta. Con `comprobando` sí: <b>nada relanza una comprobación</b>, ni al
     * entrar ni al cargar. Salir mientras `refrescarBase` viajaba y volver dejaba el aviso del
     * desenlace en «Comprobando…», escondiendo «Volver a comprobar», «Volver a publicar» y
     * «Descartar» —y sin ninguna de las tres no hay forma de cerrar el intento—
     * (revisión CODEX del PR #1725).
     *
     * Por eso lo llaman los cuatro sitios que se quedan con este carril —`salir`, `cargar`,
     * `refrescarBase` y `enviar`— en vez de arreglar solo la salida: los otros tres descartan la
     * lectura en vuelo exactamente igual.
     *
     * `publicando` NO entra aquí: no es una lectura. Un POST descartado sí ha podido cambiar el
     * servidor, tiene su `descartada` para cerrar el intento y su `.then()` lo suelta sin condición.
     */
    tomarCarrilDeLaVigente: function() {
      this.carril.invalidar();
    },
    /**
     * El historial, por su carril propio y con su fallo propio.
     *
     * Es la tabla de abajo, y vale por sí sola: que no se pueda releer no dice nada de la versión
     * vigente, que es lo de arriba. Antes viajaba encadenada a la petición de la vigente y
     * compartía su `fallo`, y eso hacía dos destrozos: al publicar, un fallo del historial
     * contaba como «la publicación ha fallado» cuando la versión ya estaba escrita y no se puede
     * retirar; y en la relectura del conflicto se quedaba sin decidir nada, así que el botón
     * volvía a habilitarse con la base vieja y el reintento chocaba igual.
     *
     * <b>Se lanza siempre desde el `exito` de quien la necesita, nunca encadenada dentro de la
     * promesa que va al carril.</b> Encadenada, la lanzaba también una operación que ya estaba
     * descartada —y entonces su lectura era la última del carril del historial y pintaba la tabla—:
     * al volver a entrar mientras se publicaba, eso dejaba la tabla en v2 con la etiqueta y el área
     * todavía en v1 (revisión CODEX del PR #1725).
     *
     * <b>Y cada lectura marca la tabla como pendiente al empezar</b>, igual que la carga de la
     * vigente pone su «Cargando…» en la etiqueta: lo que se enseña mientras la petición viaja es la
     * tabla de antes, y la etiqueta de arriba ya puede haber avanzado —al publicar, `aplicarVigente`
     * pasa a «Versión 2» y esto se pide detrás—. Sin marcarlo, `historialCargado` e
     * `historialDesfasado` se quedaban con su valor anterior y la tabla seguía afirmando con una
     * lectura vieja: «Versión 2» arriba junto a una tabla que terminaba en v1, o un aviso de
     * desfase con su reintento mientras ya se estaba reintentando (revisión CODEX del PR #1725).
     *
     * No rechaza nunca: el carril resuelve siempre.
     */
    historialAlDia: function() {
      const self = this;
      // El «Actualizando…» lo enciende y lo apaga el carril (ver `created`).
      return self.carrilHistorial.lanzar(RhNormasVacaciones.historial(), {
        exito: function(versiones) {
          self.historial = versiones || [];
          self.historialCargado = true;
          self.historialDesfasado = false;
        },
        // La tabla se queda como estaba: vaciarla haría pasar un fallo de red por «no hay
        // versiones publicadas», que en una pantalla de trazabilidad es lo contrario de la verdad.
        fallo: function() {
          self.historialDesfasado = true;
        }
      });
    },
    /**
     * <b>Se toma el carril del historial</b>: lo que hubiera en vuelo queda descartado y, con ello,
     * el aviso de que la tabla está actualizándose.
     *
     * Es la misma forma del fallo que `tomarCarrilDeLaVigente`: una lectura descartada no suelta
     * nada —sus manejadores ya no se llaman, y una conexión colgada no termina nunca—, así que el
     * «Actualizando el historial…» se quedaba puesto esperando a una lectura que ya no cuenta. Desde
     * la #472 lo apaga el carril al invalidar (ver `created`).
     *
     * Lo llaman los tres sitios que se quedan con este carril: `salir`, el fallo de `cargar` —que
     * además vacía la tabla— y `enviar`.
     */
    tomarCarrilDelHistorial: function() {
      this.carrilHistorial.invalidar();
    },
    /**
     * La versión vigente, y detrás el historial: son las dos mitades de lo mismo —el historial sin
     * la vigente deja la etiqueta de arriba sin decir cuál de las filas está en vigor—, pero cada
     * una con su carril y su fallo, y el de abajo se pide desde el `exito` del de arriba, nunca
     * encadenado dentro (ver `historialAlDia`).
     *
     * Mientras dura, los campos y el botón quedan deshabilitados: esta carga acaba en
     * `aplicarVigente`, que pisa el área de texto con lo que venga del servidor.
     */
    cargar: function() {
      const self = this;
      self.cargaFallida = false;
      // Esta carga se queda con el carril, así que es ella la que decide el desenlace: lo que
      // hubiera en vuelo —otra carga, o la relectura de una comprobación— deja de contar y sus
      // avisos con él (ver `tomarCarrilDeLaVigente`). Sin esto, `soltarBorrador` y una entrada
      // tardía descartaban una comprobación y dejaban «Comprobando…» puesto para siempre.
      self.tomarCarrilDeLaVigente();
      // Lo enciende esta carga y lo apaga el carril cuando contesta la vigente (ver `created`).
      self.cargando = true;
      self.carril.lanzar(
        // Con un intento puesto esta lectura pregunta por ÉL, no solo por la vigente: es la única
        // que puede decir si lo que se mandó llegó a publicarse (ver `lecturaQueDecide`).
        self.lecturaQueDecide(),
        {
          exito: function(lectura) {
            self.error403 = false;
            // Con una publicación pendiente de desenlace —se salió de la pantalla mientras el POST
            // viajaba y su respuesta se descartó, o la relectura del fallo no llegó a contestar—
            // esta lectura es la que dice en qué quedó, y lo decide `aplicarDesenlace`: el MISMO
            // sitio que la relectura del fallo, para que una entrada tardía no haga otra cosa.
            //
            // Sin eso, esta carga llamaba a `aplicarVigente` a secas y pisaba el borrador con el
            // texto del servidor: una recarga descartaba lo escrito sin que nadie lo decidiera
            // (revisión CODEX del PR #1725, ronda 8).
            //
            // La decisión se toma con ESTA lectura y nada más, sin esperar a la tabla de abajo:
            // encadenarla añadía una segunda petición de la que depende el desbloqueo de la
            // pantalla, y con ella una carrera —mientras se espera, se puede pedir otra
            // comprobación, y entonces hay dos lecturas queriendo decidir lo mismo—.
            self.aplicarDesenlace(lectura);
            // La tabla, detrás y por su carril: es la otra mitad de la pantalla, pero no bloquea
            // los campos —lo que los bloqueaba era `aplicarVigente`, que ya ha pasado— ni se deja
            // encadenar aquí dentro (ver `historialAlDia`).
            self.historialAlDia();
          },
          // El texto que se pinta tiene que ser el de la versión vigente o nada: dejar en el área
          // lo de la carga anterior y publicar desde ahí escribiría una versión basada en un
          // texto que ya no se sabe de cuándo es, y publicada no se puede retirar.
          //
          // Con un intento sin desenlace no se vacía nada: el borrador es la instantánea de lo que
          // se mandó, y esta carga era precisamente la que iba a averiguar si entró. Lo que queda
          // es el aviso de que no se ha podido comprobar, con su reintento (`baseSinComprobar`),
          // igual que cuando falla la relectura del fallo del POST.
          limpiar: function() {
            if (self.intento) {
              self.baseSinComprobar = true;
              self.restaurarIntento();
              return;
            }
            self.version = null;
            self.fechaPublicacion = null;
            self.texto = '';
            self.textoOriginal = '';
            self.historial = [];
            // Aquí el que falló fue el de arriba, así que el historial no se ha leído: se
            // invalida el carril para que una lectura suya en vuelo no repinte la tabla que esto
            // acaba de vaciar, y se quita el aviso, que sería de una tabla que ya no se enseña.
            // Y la tabla deja de contar como leída: vaciada por el fallo de otra cosa, no puede
            // decir «sin versiones publicadas». Ni que está actualizándose, que es lo mismo visto
            // desde el otro lado: lo que haya en vuelo deja de contar (ver `tomarCarrilDelHistorial`).
            self.tomarCarrilDelHistorial();
            self.historialCargado = false;
            self.historialDesfasado = false;
            self.cargaFallida = true;
            // Sin texto delante no hay borrador que reconciliar, y dejar los avisos puestos sería
            // bloquear el botón por un conflicto con una versión que ya no se está enseñando. El
            // reintento que queda es el de la etiqueta de arriba, que recarga la pantalla entera.
            self.conflicto = null;
            self.baseSinComprobar = false;
          },
          fallo: function(e) { self.tratarError(e); }
        }
      );
    },
    /**
     * La base y el texto se mueven juntos, siempre: es el único sitio que toca `version` y `texto`
     * a la vez, y por eso es también el que cierra el aviso de conflicto. Cuando el texto delante
     * es el de la versión que hay en vigor, no hay nada que reconciliar.
     */
    aplicarVigente: function(vigente) {
      this.version = (vigente && vigente.version) || null;
      this.fechaPublicacion = (vigente && vigente.fechaPublicacion) || null;
      this.texto = ((vigente && vigente.normas) || []).join('\n');
      this.textoOriginal = this.texto;
      this.conflicto = null;
      // Esto ES la base comprobada: lo que viene del servidor y el texto de delante son lo mismo.
      this.baseSinComprobar = false;
      // Y con la base y el texto cuadrados con el servidor no queda ninguna publicación de la que
      // averiguar el desenlace: lo que se mandó ya está contado, en un sentido o en el otro.
      this.intento = null;
    },
    /**
     * El intento es una instantánea: mientras no se sepa su desenlace, el texto y el motivo que se
     * mandaron son los que hay delante. Esto los devuelve si algo los hubiera tocado.
     *
     * Es el cinturón de la opción 1, no su mecanismo: lo que impide que se toquen es
     * `camposBloqueados`. Está porque la forma del fallo que se repitió cuatro rondas era
     * precisamente «un camino que pisa el borrador sin saberlo», y aquí hay un sitio —el `limpiar`
     * de una carga fallida— por el que la pantalla pasa sin haber decidido nada.
     */
    restaurarIntento: function() {
      if (!this.intento) return;
      this.texto = this.intento.texto;
      this.motivo = this.intento.motivo;
    },
    /**
     * El POST ha terminado: ya no hay ninguna escritura viajando <b>por el navegador</b>, así que a
     * partir de aquí una lectura ya puede aportar algo. Se llama en los TRES finales posibles del
     * POST —bien, mal y descartado— y es lo único que levanta `enVuelo`: un camino que se olvidara
     * dejaría la pantalla esperando para siempre a una publicación que ya terminó.
     *
     * @param publicada si el POST llegó a contestar que sí. Entonces el desenlace ya no hay que
     *        deducirlo de ninguna lectura, aunque su respuesta llegara fuera de turno y no se
     *        pueda pintar.
     * @param ambiguo si el POST terminó <b>sin que el servidor dijera que no había escrito</b>. Ver
     *        {@code esFalloAmbiguo}: eso deja el intento sin confirmar, no sin publicar.
     */
    cerrarEnVuelo: function(publicada, ambiguo) {
      if (!this.intento) return;
      this.intento.enVuelo = false;
      if (publicada) this.intento.confirmada = true;
      else if (ambiguo) this.intento.ambiguo = true;
    },
    /**
     * <b>Si el fallo del POST deja en duda que se haya escrito.</b> Es la diferencia que faltaba, y
     * la que se colaba por el único sitio que quedaba: «el POST ha fallado» se trataba como «el POST
     * no ha publicado», y con un 504 de la pasarela o un `status` 0 eso es falso.
     *
     * Concluyentes —el servidor leyó la petición y dijo que NO escribía— son los códigos con los que
     * el propio endpoint rechaza: 400 (sin motivo, sin normas, identidad desmedida), 401 y 403
     * (identificación y permisos), 404 (ruta) y 409 (la base ha cambiado). Todo lo demás es ambiguo
     * y hay que tratarlo así:
     *
     * - `0` o sin `status` — la conexión se cortó; la petición puede haber llegado igual;
     * - 408 y 504 — plazo agotado en el camino, con el servidor pudiendo seguir escribiendo;
     * - 500, 502, 503 — y el 500 no es teórico aquí: donde no hay transacciones, el `insert` de la
     *   versión puede entrar y fallar el apunte de auditoría detrás, y eso sale como 500 con la
     *   versión publicada.
     *
     * Sin esta distinción, la relectura siguiente veía la base todavía en su sitio, concluía
     * `'intacta'`, soltaba la identidad del intento y desbloqueaba la edición; si el POST original
     * confirmaba después, se publicaba en silencio una instantánea que ya no era la que la persona
     * tenía delante, y una versión publicada no se borra (I6) (revisión CODEX del PR #1725).
     */
    esFalloAmbiguo: function(e) {
      const status = (e && (e.status || (e.xhr && e.xhr.status))) || 0;
      return [400, 401, 403, 404, 409].indexOf(status) < 0;
    },
    /**
     * La identidad de un intento de publicación: una cadena que no se repite.
     *
     * De ella depende todo el reconocimiento posterior, así que el azar sale de
     * `crypto.getRandomValues`, que la tienen todos los navegadores de la ERP. El instante va
     * delante para que dos intentos del mismo segundo no dependan solo del azar, y el prefijo
     * `nv-` deja ver de dónde salió si alguien se la encuentra en la base de datos.
     *
     * <b>Sin respaldo de `Math.random`.</b> Lo tenía, y sobra por dos motivos: `Math.random` no
     * promete reparto ni independencia entre pestañas —es un generador pseudoaleatorio, y Sonar lo
     * marca como punto de seguridad (javascript:S2245) con razón, porque de esta cadena depende que
     * una publicación no se duplique—, y el respaldo nunca se ejecuta en los navegadores que usa la
     * ERP. Donde no hubiera `crypto` se usa un contador de la propia pantalla, que dentro de una
     * pestaña no se repite nunca; dos pestañas a la vez sin `crypto` es el único hueco, y entre dos
     * pestañas que publiquen lo mismo en el mismo milisegundo sigue habiendo el `versionBase` del
     * backend, que deja pasar solo una.
     */
    nuevaIdentidadDeIntento: function() {
      const cripto = typeof window !== 'undefined' && window.crypto;
      let azar;
      if (cripto && cripto.getRandomValues) {
        const bytes = new Uint8Array(12);
        cripto.getRandomValues(bytes);
        azar = Array.prototype.map
          .call(bytes, function(b) { return ('0' + b.toString(16)).slice(-2); })
          .join('');
      } else {
        azar = 'c' + (++this.intentosLanzados);
      }
      return 'nv-' + Date.now().toString(36) + '-' + azar;
    },
    /**
     * <b>La lectura que decide el desenlace</b>, en UNA sola petición y siempre la misma forma:
     * `{ vigente, publicada, version }`.
     *
     * Con un intento puesto se pregunta por él —`GET /intentos/{id}`—, y esa respuesta trae las dos
     * mitades que hacen falta: si ese intento tiene una versión a su nombre **en todo el historial**
     * y, aparte, qué hay en vigor ahora. Sin intento es una carga normal y basta la vigente.
     *
     * Antes se preguntaba siempre por la vigente, y eso dejaba fuera el caso de que la publicación
     * propia entrara y otra persona publicase encima: la identidad se quedaba en una fila del
     * historial y la pantalla la daba por ajena. Buscarla en el historial desde aquí habría pedido
     * una segunda lectura —y con ella la carrera que esta pantalla lleva cerrando—, así que la
     * respuesta la junta el servidor y aquí sigue habiendo una sola petición
     * (revisión CODEX del PR #1963).
     */
    lecturaQueDecide: function() {
      const intento = this.intento;
      if (intento && intento.id) {
        return RhNormasVacaciones.desenlaceIntento(intento.id).then(function(d) {
          return {
            vigente: (d && d.vigente) || null,
            publicada: !!(d && d.publicada),
            version: (d && d.version) || null
          };
        });
      }
      return RhNormasVacaciones.vigente().then(function(vigente) {
        return { vigente: vigente || null, publicada: false, version: null };
      });
    },
    /**
     * Si lo que se mandó está publicado. Es la pregunta que antes se contestaba comparando el texto,
     * y la que ahora contesta la identidad: dos personas pueden publicar el mismo texto con motivos
     * distintos, pero una identidad no se repite.
     *
     * La contesta el servidor (`publicada`), que la busca por identidad en todo el historial, así
     * que una publicación propia enterrada bajo otra también se reconoce. La identidad en la vigente
     * queda como respaldo: sirve para la lectura que no preguntó por ningún intento.
     */
    laPublicoMiIntento: function(lectura) {
      const id = this.intento && this.intento.id;
      if (!id || !lectura) return false;
      if (lectura.publicada) return true;
      return !!lectura.vigente && lectura.vigente.intentoId === id;
    },
    /**
     * <b>Qué ha pasado con la publicación</b>, a la vista de lo que el servidor dice que está en
     * vigor. Es el ÚNICO sitio donde se decide, y por aquí pasan las tres relecturas que hay
     * después de escribir: la del fallo del POST, la de «volver a comprobar» y la carga que sigue a
     * un POST descartado. Repartida por cada llamante, cada uno aprendía a distinguir un caso y se
     * dejaba el siguiente (revisión CODEX del PR #1725).
     *
     * - `'sin-intento'` — no se había mandado nada: la lectura es una carga normal.
     * - `'aplicada'` — es la publicación propia, y lo dice **su identidad**: o el POST llegó a
     *   contestar que sí (`confirmada`), o lo que hay publicado lleva el `id` de este intento. Un
     *   POST puede escribir y no contestar (un 504 de la pasarela, una conexión que se corta y deja
     *   `status` 0), y entonces el fallo del POST no significa que no se publicara.
     * - `'pendiente'` — **el POST sigue en vuelo**. Lo que hay en vigor ahora no dice en qué va a
     *   quedar una escritura que todavía viaja: que siga estando la base no significa que no vaya a
     *   entrar. Así que no se decide nada y el intento no se cierra. Sin este caso, salir y volver
     *   mientras se publicaba cerraba el intento como `'intacta'`, y cuando el POST acababa fallando
     *   la carga siguiente ya no veía ningún intento y pisaba el borrador con el texto del servidor
     *   (revisión CODEX del PR #1725).
     * - `'sin-confirmar'` — **el POST terminó mal, pero de una manera que no dice que no escribiera**
     *   (ver `esFalloAmbiguo`), y esta lectura todavía no ve la identidad publicada. Eso NO es un
     *   desenlace: es «no lo sé». Que en vigor siga estando la base tampoco lo cierra, porque la
     *   escritura puede estar aún en curso en el servidor. Así que el intento tampoco se cierra:
     *   sigue siendo una instantánea inmutable y lo que se ofrece es volver a comprobar o reenviar
     *   **el mismo intento**. Antes esto caía en `'intacta'`, se soltaba la identidad y se
     *   desbloqueaba la edición; si el POST confirmaba después, se publicaba en silencio una
     *   instantánea que ya no era la que había delante (revisión CODEX del PR #1725).
     * - `'intacta'` — el POST terminó sin publicar —y eso lo dijo el servidor, con un 400, un 403 o
     *   un 409— y en vigor sigue estando la base: se puede reintentar.
     * - `'ajena'` — hay otra versión y no lleva esta identidad. El borrador no la incluye, así que
     *   no se avanza la base: eso es el conflicto que hay que decidir a mano. **Un texto idéntico no
     *   la convierte en propia**: dos personas pueden publicar lo mismo con motivos distintos, y
     *   darla por propia borraba el motivo de quien perdió la carrera sin que su cambio quedara en
     *   ningún sitio (dirección, 01/10/2026).
     */
    desenlace: function(lectura) {
      const intento = this.intento;
      if (!intento) return 'sin-intento';
      if (intento.confirmada || this.laPublicoMiIntento(lectura)) return 'aplicada';
      if (intento.enVuelo) return 'pendiente';
      // El orden importa: esto va DELANTE de la comparación de bases, porque con un final ambiguo la
      // base donde estaba no demuestra nada. Lo que lo saca de aquí es ver la identidad publicada
      // —arriba— o una decisión de la persona, no que pase el tiempo.
      if (intento.ambiguo) return 'sin-confirmar';
      // Como número y con el 0 de «no hay ninguna versión publicada», que es el mismo criterio con
      // el que viajó `versionBase` al backend: así se pueden comparar sin más.
      const enVigor = (lectura && lectura.vigente && lectura.vigente.version) || 0;
      return enVigor === intento.base ? 'intacta' : 'ajena';
    },
    /**
     * <b>Qué se hace con el desenlace</b>, que es lo que decide `desenlace`. Las dos relecturas
     * posteriores a una publicación —la del fallo del POST y la carga de una entrada posterior—
     * pasan por aquí, y por eso una recarga tardía no puede hacer algo distinto de lo que haría la
     * relectura: esa diferencia era el agujero por el que se perdía el borrador.
     *
     * El borrador NO se toca en ningún caso salvo cuando la publicación consta como hecha —y eso lo
     * dice la identidad del intento, no el parecido de los textos—. El intento se cierra en todos
     * los desenlaces menos en uno: con el POST todavía en vuelo (`'pendiente'`) no se cierra nada,
     * porque aún no hay desenlace que aplicar. Donde se cierra es donde se desbloquean los campos.
     *
     * @return lo que ha pasado, por si quien llama tiene algo más que decir.
     */
    aplicarDesenlace: function(lectura) {
      const vigente = lectura && lectura.vigente;
      const queHaPasado = this.desenlace(lectura);
      if (queHaPasado === 'sin-intento') {
        // No hay nada que decidir: es una carga normal y lo que se lee es lo que se pinta.
        this.aplicarVigente(vigente);
        return queHaPasado;
      }
      if (queHaPasado === 'pendiente' || queHaPasado === 'sin-confirmar') {
        // Las dos son lo mismo visto desde aquí: todavía no hay desenlace que aplicar. En
        // `'pendiente'` el POST sigue viajando; en `'sin-confirmar'` terminó de una manera que no
        // dice si escribió. En ninguna de las dos se cierra el intento, se toca el borrador ni se
        // sueltan los campos. Y de lo que sí se ha enterado esta lectura —que al servidor se
        // llega— se quita el aviso de «no se ha podido comprobar».
        this.restaurarIntento();
        this.baseSinComprobar = false;
        return queHaPasado;
      }
      if (queHaPasado === 'aplicada') {
        // El borrador ya está publicado, así que lo que queda delante es lo que hay en vigor —que
        // puede ser la versión propia o una posterior, si alguien publicó encima— y el motivo se
        // limpia como en cualquier publicación: el que se escribió está guardado en su versión.
        this.aplicarVigente(vigente);
        this.anotarPublicada();
        return queHaPasado;
      }
      // No se publicó lo que se mandó. El texto y el motivo se quedan como están: son trabajo
      // escrito a mano, y aquí nadie ha pedido descartarlo.
      if (queHaPasado === 'intacta') {
        // En vigor sigue la base que se mandó: el fallo era otra cosa (un 400, un corte de red
        // antes de escribir). Se refresca la foto del servidor alrededor del borrador —la fecha y
        // el texto de partida— y se puede volver a publicar sin más.
        this.version = (vigente && vigente.version) || null;
        this.fechaPublicacion = (vigente && vigente.fechaPublicacion) || null;
        this.textoOriginal = ((vigente && vigente.normas) || []).join('\n');
        this.conflicto = null;
      } else {
        // Otra persona publicó por delante. NO se avanza la base —ni la etiqueta de arriba, que es
        // la misma cosa—: el texto del área sale de la versión anterior y no lleva su cambio, así
        // que publicarlo contra la base nueva lo pasaría como versión buena y dejaría el cambio de
        // la otra persona deshecho, sin vuelta atrás porque una versión publicada no se borra (I6).
        // Se aparta y se pide una decisión: mirar esa versión e incorporar lo que falte
        // (`asumirConflicto`) o descartar el borrador (`descartarBorrador`).
        this.conflicto = {
          version: (vigente && vigente.version) || null,
          fechaPublicacion: (vigente && vigente.fechaPublicacion) || null,
          texto: ((vigente && vigente.normas) || []).join('\n')
        };
      }
      // El desenlace ya se sabe: se suelta la instantánea y los campos se vuelven a poder editar
      // —hace falta, porque incorporar el cambio de la otra persona se hace escribiendo—.
      this.intento = null;
      this.baseSinComprobar = false;
      return queHaPasado;
    },
    /**
     * Una publicación que consta como hecha, llegue su respuesta o no llegue: se limpia el motivo
     * —es de la publicación que ya está— y se dice. En un solo sitio porque son cuatro los caminos
     * que acaban aquí, y el aviso tiene que ser el mismo en los cuatro.
     */
    anotarPublicada: function() {
      this.motivo = '';
      this.faltaMotivo = false;
      this.intento = null;
      this.$f7.toast.create({
        text: this.$t('normasvacaciones.publicada'),
        closeTimeout: 2500
      }).open();
    },
    /**
     * Relectura después de una publicación que ha fallado: qué hay en vigor de verdad y el
     * historial, SIN tocar el área de texto, que es trabajo escrito a mano.
     *
     * Hace falta porque un POST que falla deja de garantizar que lo que hay delante siga siendo lo
     * vigente. Se relee en cualquier fallo y no solo en el 409 porque la lista de códigos en los
     * que la base puede haber quedado vieja no es cerrada: un 504 de la pasarela también puede
     * llevar el cambio aplicado por detrás.
     *
     * Los tres resultados los decide `desenlace`, en un solo sitio, y aquí solo se obra en
     * consecuencia:
     *
     * - **La publicación entró igual** (`'aplicada'`). Un POST puede escribir y no contestar, así
     *   que su fallo no dice que no se publicara. Si lo que hay en vigor es la versión siguiente a
     *   la base que se mandó, con el texto que se mandó, la publicación está hecha: es el mismo
     *   final que si la respuesta hubiera llegado, y el error del POST **no se enseña**, porque
     *   decir que ha fallado algo que está hecho es lo que empuja a volver a publicarlo.
     * - **La vigente sigue siendo la misma base** (`'intacta'`). El fallo era otra cosa (un 400, un
     *   corte de red antes de escribir): se refresca la fecha y el texto de partida y se puede
     *   volver a publicar sin más.
     * - **La vigente es otra** (`'ajena'`), o sea otra persona publicó por delante. Aquí NO se
     *   avanza la base: el texto del área sale de la versión anterior y no lleva su cambio, así que
     *   publicarlo contra la base nueva lo pasaría como versión buena y dejaría el cambio de la otra
     *   persona deshecho —sin vuelta atrás, porque una versión publicada no se borra (I6)—. Se
     *   aparta en `conflicto`, se bloquea el botón y se pide una decisión explícita: mirar esa
     *   versión e incorporar lo que falte (`asumirConflicto`) o descartar el borrador
     *   (`descartarBorrador`). Avanzar la base sola era convertir la protección optimista en un
     *   retroceso silencioso.
     *
     * Y un cuarto, el que falta hasta de los de arriba: **la relectura tampoco contesta**. Ahí no se
     * sabe cuál de los tres casos es, así que no se puede publicar —reintentar mandaría el mismo
     * `versionBase` y chocaría igual—, pero tampoco se puede dar por perdido el borrador. Se deja el
     * botón bloqueado con `baseSinComprobar` y se ofrece otra comprobación, que no toca el área de
     * texto. Sin esto, el carril resolvía sin ejecutar ningún manejador: `publicando` volvía a
     * `false`, el botón se habilitaba con la base vieja y cada reintento chocaba otra vez, y la
     * única salida era salir y volver a entrar perdiendo lo escrito.
     *
     * Va por el carril como las cargas: si mientras viajaba se ha salido o se ha recargado, esta
     * relectura es la vieja y pintarla devolvería la base a la versión de antes.
     *
     * @param errorDelPost el fallo que obligó a releer, si lo hubo. Se enseña AQUÍ y no antes:
     *        hasta saber qué hay en vigor no se sabe si ese error significa que no se publicó. Si
     *        esta relectura queda descartada —se salió de la pantalla o se recargó— no se enseña
     *        nada, mismo criterio que para un POST descartado: lo que aclara qué pasó es la pantalla
     *        releída, no el error de una acción que se abandonó hace rato.
     */
    refrescarBase: function(errorDelPost) {
      const self = this;
      // Igual que `cargar`: esta relectura se queda con el carril, así que lo que hubiera en vuelo
      // deja de contar y no puede quedarse un aviso esperándolo (ver `tomarCarrilDeLaVigente`).
      self.tomarCarrilDeLaVigente();
      // Los campos siguen bloqueados mientras esto viaja, y por eso se dice aquí: el desenlace se
      // está decidiendo, así que es justo el rato en el que no puede haber dos manos sobre el mismo
      // texto (opción 1, dirección 01/10/2026).
      self.comprobando = true;
      // Y el aviso de la comprobación ANTERIOR se suelta al empezar esta, que es el mismo olvido que
      // el del historial: `baseSinComprobar` es «no se ha podido comprobar qué versión está en
      // vigor», y mientras una comprobación nueva viaja eso ya no es cierto. Dejándolo puesto,
      // pulsar «Volver a comprobar» no cambiaba una palabra del aviso —seguía diciendo que no se
      // había podido— aunque debajo apareciera «Comprobando…». Si esta también falla, su `fallo` lo
      // vuelve a poner; si va bien, lo sueltan igual `aplicarDesenlace` y `aplicarVigente`.
      self.baseSinComprobar = false;
      return self.carril.lanzar(
        self.lecturaQueDecide(),
        // Sin `limpiar`: vaciar aquí borraría la base que se tenía delante, que sigue sirviendo, y
        // con ella el texto escrito a mano.
        {
          exito: function(lectura) {
            // El historial va por su carril y al lado de la decisión, no dentro: es lo que deja ver
            // qué versión entró por delante en un 409, pero si no se puede releer esta relectura
            // tiene que decidir igual. Encadenado a él, un historial caído dejaba el conflicto sin
            // apartar y el botón se volvía a habilitar con la base vieja, o sea el mismo 409 otra
            // vez.
            self.historialAlDia();
            // Qué ha pasado y qué se hace con ello, los dos en su sitio único. Si la publicación
            // entró igual, el error del POST NO se enseña: decir que ha fallado algo que está hecho
            // es lo que empuja a volver a publicarlo.
            // El error del POST no se enseña si la publicación entró igual —decir que ha fallado
            // algo que está hecho es lo que empuja a volver a publicarlo— ni si el desenlace sigue
            // sin saberse: ahí lo que hay que leer es el aviso de la pantalla, que es persistente y
            // lleva al lado qué se puede hacer. Un modal que diga «error 504» encima de eso solo
            // tapa las acciones.
            const queHaPasado = self.aplicarDesenlace(lectura);
            if (queHaPasado !== 'aplicada' && queHaPasado !== 'sin-confirmar' && errorDelPost) {
              self.tratarError(errorDelPost);
            }
          },
          // Aquí sí se enseña el error del POST: es lo único que se sabe. El aviso de que la base
          // no se ha podido comprobar va aparte, en la pantalla y no en un modal, porque es
          // persistente y lleva una acción al lado. Y el intento NO se cierra: el desenlace sigue
          // siendo desconocido, así que los campos siguen bloqueados y la instantánea intacta.
          fallo: function() {
            if (errorDelPost) self.tratarError(errorDelPost);
            self.baseSinComprobar = true;
          }
        }
      );
    },
    /**
     * «Volver a comprobar»: otra relectura de qué hay en vigor, sin tocar el área de texto. Es la
     * salida de `baseSinComprobar`, y la única: lo que desbloquea el botón es saber cuál es la
     * base, no que haya pasado un rato.
     */
    reintentarComprobacion: function() {
      // Con una carga, una publicación o otra comprobación en vuelo no se pide nada: las tres
      // acaban decidiendo ellas cuál es la base, y colarles una relectura por delante las
      // descartaría a medias.
      if (this.cargando || this.publicando || this.comprobando) return;
      this.refrescarBase();
    },
    /**
     * «Volver a publicar» cuando el desenlace sigue sin confirmarse: **reenvía el mismo intento**,
     * con su identidad, su texto, su motivo y su base. Es la salida del estado `'sin-confirmar'`
     * que no depende de que el servidor acabe contestando nada.
     *
     * Reenviar es seguro justamente por la identidad: si la escritura original llegó a entrar, el
     * backend la reconoce y devuelve la versión que ya publicó en vez de escribir otra; si no entró,
     * esto la publica; y si entretanto publicó otra persona, el `versionBase` lo corta con un 409.
     * En ninguno de los tres casos se puede acabar con dos versiones del mismo texto (I6).
     */
    reintentarPublicacion: function() {
      // Nada en vuelo: una carga, una publicación o una comprobación a medias acabarían decidiendo
      // ellas el desenlace, y colarles un POST por delante las descartaría sin saber qué pasó.
      if (this.cargando || this.publicando || this.comprobando) return;
      // Y solo desde «sin confirmar». Con el POST todavía en vuelo esto sería un segundo envío del
      // mismo intento a la vez, y sin intento ambiguo no hay nada que reenviar: se publica por el
      // botón de siempre, que es el que valida motivo y texto.
      if (!this.intento || this.intento.enVuelo || !this.intento.ambiguo) return;
      this.enviar();
    },
    /**
     * «Descartar mi texto»: se vuelve a cargar la pantalla entera desde el servidor. Se recarga en
     * vez de pintar la copia apartada en `conflicto` porque esa copia puede llevar ya minutos
     * ahí —el tiempo que se haya tardado en decidir— y entre medias puede haber entrado otra
     * versión más; recargando, lo que queda delante es lo vigente de ahora.
     *
     * <b>Es la decisión explícita</b>, y lo único que puede descartar el borrador: por eso cierra
     * el intento antes de recargar. Una recarga que llegue por cualquier otra vía —volver a entrar
     * en la pantalla, un reintento de la etiqueta— se encuentra el intento puesto y respeta lo
     * escrito (ver `cargar` y `aplicarDesenlace`).
     */
    descartarBorrador: function() {
      const self = this;
      // Con el POST todavía en vuelo, no. Cerrar el intento ahí dejaría sin identidad lo que
      // pudiera entrar después, y entonces ya no habría forma de reconocerlo. La pantalla no ofrece
      // el enlace en ese rato, pero la regla vive aquí, que es donde el intento se cierra.
      if (self.intento && self.intento.enVuelo) return;
      // Y con el desenlace sin confirmar se pregunta antes, porque esto es lo único que deja un
      // cabo suelto de verdad: soltar la identidad es renunciar a reconocer una escritura que
      // todavía pudiera entrar, y si entra ya nadie podrá retirarla (I6). Se dice con esas palabras
      // en vez de esconder el enlace: la otra salida —«Volver a publicar»— puede no servirle a
      // quien ya no quiere publicar eso, y dejarla sin salida sería peor.
      if (self.intento && self.intento.ambiguo) {
        self.$f7.dialog.confirm(self.$t('normasvacaciones.descartarSinConfirmar'), function() {
          self.soltarBorrador();
        });
        return;
      }
      self.soltarBorrador();
    },
    /** El descarte en sí, una vez decidido. Aparte para que la pregunta de arriba no lo duplique. */
    soltarBorrador: function() {
      this.intento = null;
      this.conflicto = null;
      this.baseSinComprobar = false;
      this.motivo = '';
      this.faltaMotivo = false;
      this.cargar();
    },
    /**
     * «Ya he incorporado su cambio»: el borrador pasa a estar basado en la versión que entró por
     * delante. Es la acción explícita que desbloquea el reintento, y solo la puede dar una
     * persona después de haber visto esa versión: no se puede comprobar desde aquí si la fusión
     * se ha hecho de verdad, pero sí que no se publique sin haberla pedido.
     */
    asumirConflicto: function() {
      const self = this;
      self.$f7.dialog.confirm(
        self.$t('normasvacaciones.conflictoConfirmar', { version: self.conflicto.version }),
        function() {
          if (!self.conflicto) return;
          self.version = self.conflicto.version;
          self.fechaPublicacion = self.conflicto.fechaPublicacion;
          // El texto de partida es el de la versión nueva: es contra ella contra la que se compara
          // para avisar de «no has cambiado nada».
          self.textoOriginal = self.conflicto.texto;
          self.conflicto = null;
        }
      );
    },
    cambiarMotivo: function(valor) {
      this.motivo = valor || '';
      // En cuanto se escribe algo el aviso desaparece: dejarlo puesto mientras se teclea es
      // decirle a alguien que le falta justo lo que está escribiendo.
      if (this.motivo.trim()) this.faltaMotivo = false;
    },
    publicar: function() {
      const self = this;
      // Con un conflicto abierto no se publica aunque se llegue aquí por teclado o por un doble
      // toque antes de que el botón se deshabilite: lo que se mandaría es un texto que no lleva el
      // cambio de la otra persona, y publicado ya no se puede retirar.
      if (self.conflicto) {
        self.$f7.dialog.alert(self.textoConflicto);
        return;
      }
      // Y tampoco con una publicación cuyo desenlace no se sabe, aunque se llegue aquí por teclado
      // o por un doble toque: la base que se mandaría es la de ese intento, que es justo la que
      // puede haber quedado vieja, y mandar otra vez lo mismo puede acabar en dos versiones con el
      // mismo texto que ya no se podrían borrar (I6).
      if (self.desenlaceDesconocido) {
        self.$f7.dialog.alert(self.textoDesenlaceDesconocido);
        return;
      }
      // Ni con una carga en vuelo, por el mismo camino del teclado o del doble toque: todavía no
      // se sabe qué versión hay en vigor, así que la base que se mandaría es la de antes de entrar
      // —o 0— y la publicación saldría encima de lo que haya, que es lo que el 409 vigila.
      if (self.cargando) {
        self.$f7.dialog.alert(self.$t('normasvacaciones.cargando'));
        return;
      }
      // Sin motivo no se publica. El backend lo rechaza igual con un 400, pero avisar aquí es lo
      // que pide la incidencia: «Publicar sin motivo no crea versión y la pantalla lo avisa».
      if (!self.motivo.trim()) {
        self.faltaMotivo = true;
        self.$f7.dialog.alert(self.$t('normasvacaciones.faltaMotivo'));
        return;
      }
      if (!self.texto.trim()) {
        self.$f7.dialog.alert(self.$t('normasvacaciones.faltanNormas'));
        return;
      }
      if (self.texto === self.textoOriginal) {
        // No se bloquea —puede querer dejar constancia de una revisión sin cambios—, pero se
        // pregunta: la versión que salga de aquí no se podrá borrar (I6).
        self.$f7.dialog.confirm(self.$t('normasvacaciones.confirmarSinCambios'), function() {
          self.enviar();
        });
        return;
      }
      self.enviar();
    },
    enviar: function() {
      const self = this;
      self.publicando = true;
      // Publicar manda sobre cualquier carga en vuelo: lo que devuelve el POST es la vigente
      // releída DESPUÉS de escribir, así que es posterior a lo que traiga una carga que se pidió
      // antes. Y va por el carril, así que si se sale mientras el POST viaja su respuesta se
      // descarta: pintarla sería hacerlo sobre una pantalla de la que ya se ha salido, y su
      // `fallo` abriría el diálogo del error encima de otra.
      //
      // Por `tomarCarrilDeLaVigente` y no por `carril.invalidar()` a secas: lo que se descarta aquí
      // puede ser una lectura, y entonces hay que soltar su aviso además de dejar de pintarla.
      self.tomarCarrilDeLaVigente();
      // Y el del historial: la tabla que vale es la de después de publicar, no la que pidió una
      // carga anterior y aún viene de camino. Por `tomarCarrilDelHistorial` y no por `invalidar()`
      // a secas, para que su aviso no se quede esperando a esa lectura abandonada.
      self.tomarCarrilDelHistorial();
      // Qué se manda, apuntado ANTES de mandarlo: su identidad —con la que se reconoce después la
      // publicación propia si la respuesta no llega (ver `desenlace`)—, la base, y el texto y el
      // motivo tal como se escribieron, que son la instantánea que se conserva mientras el desenlace
      // sea desconocido. Nace `enVuelo`: hasta que el POST termine, ninguna lectura puede cerrarlo.
      //
      // **Un intento sin confirmar se REENVÍA, no se estrena otro.** Misma identidad, mismo texto,
      // mismo motivo y misma base, y es la pieza que cierra el fallo ambiguo: si la escritura
      // original llegara tarde al servidor, este envío la reconoce por la identidad y devuelve la
      // versión que ya publicó en vez de escribir otra. Con una identidad nueva acababan los dos
      // envíos en dos versiones, que no se podrían borrar (I6).
      if (self.intento && !self.intento.enVuelo) {
        self.intento.enVuelo = true;
        self.intento.ambiguo = false;
      } else {
        // La base viaja siempre como número, y es 0 cuando la pantalla no tenía ninguna versión
        // delante. Mandar `null` ahí era pedirle al backend que no comprobase nada: si otra persona
        // publicaba la v1 mientras esta pantalla estaba abierta y vacía, esto entraba como v2 y su
        // v1 se quedaba enterrada sin que nadie lo dijera. 0 es «no había ninguna», que sí se puede
        // comparar con lo que haya en vigor.
        self.intento = {
          id: self.nuevaIdentidadDeIntento(),
          base: self.version || 0,
          texto: self.texto,
          motivo: self.motivo,
          enVuelo: true,
          confirmada: false,
          ambiguo: false
        };
      }
      // Lo que viaja es la instantánea, no lo que haya en los campos: en un envío nuevo son lo
      // mismo, y en un reenvío la instantánea es justo lo que no se ha podido editar.
      const intento = self.intento;
      self.carril.lanzar(
        RhNormasVacaciones.publicar(intento.texto, intento.motivo.trim(), intento.base, intento.id),
        {
          exito: function(desenlace) {
            self.cerrarEnVuelo(true);
            // La respuesta trae las dos mitades: qué versión publicó este intento y cuál está en
            // vigor ahora. Lo que se pinta es la vigente, que no es necesariamente la que se acaba
            // de escribir —si otra persona publicó por detrás, lo que queda en pantalla es lo que
            // está en vigor de verdad—.
            self.aplicarVigente(desenlace && desenlace.vigente);
            // La tabla, detrás y por su carril propio (ver `historialAlDia`): un historial caído
            // detrás de un POST que SÍ escribió no puede contar como «la publicación ha fallado»,
            // porque es irreversible.
            self.historialAlDia();
            self.anotarPublicada();
          },
          // Sin `limpiar`: un POST rechazado no invalida lo que hay en pantalla —el backend no ha
          // escrito nada—, y vaciarlo se llevaría por delante el texto escrito a mano.
          fallo: function(e) {
            // El POST ha terminado, aunque haya terminado mal. Y se apunta DE QUÉ manera: con un
            // 400, un 403 o un 409 el servidor ha dicho que no escribía, y entonces una lectura ya
            // puede cerrar el desenlace; con un 504, un `status` 0 o un 500 no lo ha dicho, y el
            // intento se queda sin confirmar hasta que se vea su identidad publicada o la persona
            // decida (ver `esFalloAmbiguo`).
            self.cerrarEnVuelo(false, self.esFalloAmbiguo(e));
            // Un 409 es «alguien publicó mientras editabas»: hay que recargar para no volver a
            // mandar lo mismo, pero sin pisar el texto escrito a mano, que es trabajo de la
            // persona. Se relee la base junto con el historial, que es lo que deja ver qué versión
            // entró por delante.
            //
            // Y el error va CON la relectura, que es la que lo enseña: un POST puede haber escrito
            // sin contestar (un 504, una conexión cortada), así que hasta saber qué hay en vigor no
            // se sabe si esto fue un fallo. Enseñándolo aquí, una publicación que sí entró se
            // anunciaba como fallida y acto seguido como conflicto de otra persona.
            //
            // <b>Sin esperarla para soltar el botón.</b> Antes el `.then()` de abajo encadenaba esta
            // relectura, y era la MISMA forma del fallo que `comprobando` un paso más adentro: si su
            // conexión se quedaba colgada, `publicando` no se soltaba nunca, el aviso del desenlace
            // —que pide `!publicando`— no se pintaba, y «Volver a comprobar» y «Volver a publicar»
            // se negaban a lanzar nada porque las dos mirán `publicando`. La pantalla se quedaba con
            // el área bloqueada y sin un solo sitio donde pulsar, sin ni salir de ella
            // (revisión CODEX del PR #1725, ronda 2).
            //
            // Y ya no hace falta: lo que impide reintentar con una base que puede estar vieja es el
            // intento sin desenlace —`camposBloqueados` por `desenlaceDesconocido`, y `publicar` lo
            // corta aparte—, no este botón. El rato que dura la relectura lo dice `comprobando`.
            self.refrescarBase(e);
          },
          // Un POST descartado NO es como una lectura descartada. La lectura que llega tarde no
          // deja nada pendiente, porque la vigente pinta lo bueno; esta ya ha cambiado el
          // servidor. Se descarta al salir de la pantalla mientras publicaba, y si se vuelve antes
          // de que termine, la carga del `beforein` leyó la versión ANTERIOR: sin esto la pantalla
          // se quedaba en v1 con la v2 ya publicada, y sin decir que se había publicado.
          //
          // Si no se ha vuelto no hay nada que hacer: entrar vuelve a cargar. Y en el fallo se
          // recarga igual —un POST rechazado no garantiza que la base siga siendo la de antes—,
          // pero sin diálogo: lo que aclara qué pasó es la pantalla releída, no un error de una
          // acción que se abandonó hace rato. Si ese POST había escrito sin contestar, lo dice la
          // carga, que mira el intento con el mismo `desenlace` que la relectura del fallo.
          descartada: function(ok, e) {
            // Lo primero, y pase lo que pase con la pantalla: el POST ha terminado. Si esto se
            // dejara para cuando hay alguien delante, salir mientras se publicaba y no volver
            // dejaría el intento «en vuelo» para siempre, y la pantalla congelada al volver a
            // entrar sin nada que la pudiera desbloquear.
            //
            // Y el fallo se clasifica igual que en `fallo`: un POST descartado que además falló de
            // forma ambigua sigue siendo ambiguo, aunque su respuesta ya no sirva para pintar.
            self.cerrarEnVuelo(ok, !ok && self.esFalloAmbiguo(e));
            // Sin pantalla delante no hay nada que pintar: al volver a entrar, la carga mira la
            // identidad del intento y lo reconoce igual.
            if (!self.activa) return;
            // Y con pantalla delante se recarga, sin anunciar nada todavía: lo que hay delante es
            // anterior a este POST. Quien dice si se publicó es el mismo sitio de siempre, con la
            // identidad del intento —y `confirmada` ya puesta si el POST llegó a contestar que sí—.
            self.cargar();
          }
        }
      ).then(function() {
        // En cuanto el POST termina, y sin esperar a nada más: esto dice que hay una ESCRITURA
        // viajando, y ya no la hay. Lo que venga después —la relectura que averigua el desenlace—
        // tiene su propio aviso (`comprobando`) y su propio bloqueo (`desenlaceDesconocido`).
        //
        // Sin condición: el botón tiene que volver a habilitarse también cuando la publicación ha
        // quedado descartada por una carga posterior. Si no, al volver a entrar la pantalla sigue
        // viva —la ruta es `keepAlive`— y el botón se queda bloqueado para siempre.
        self.publicando = false;
      });
    },
    /**
     * El texto de una versión del historial, solo lectura.
     *
     * Lo que decide si una respuesta vale no es el número de versión, sino **ser la de la apertura
     * que está delante**: cerrar la ventana mientras cargaba y volver a abrir la MISMA fila daba
     * dos llamadas con el mismo número, así que un timeout tardío de la primera pasaba la
     * comprobación, cerraba la ventana nueva y enseñaba un error aunque la segunda hubiera cargado
     * bien (revisión CODEX del PR #1725). Por eso cada apertura se numera, y `cerrarVer` invalida
     * la que haya en vuelo: lo que termina sobre una ventana ya cerrada no tiene nada que decir.
     *
     * No va por `carrilPeticiones` porque no se descarta por «no ser la última» sin más: el número
     * de versión también tiene que cuadrar para pintar el texto bajo su título.
     */
    ver: function(numero) {
      const self = this;
      const mia = ++self.ultimaVista;
      self.numeroVersionVista = numero;
      self.versionVista = null;
      self.verAbierto = true;
      RhNormasVacaciones.version(numero).then(function(version) {
        if (!self.verAbierto || mia !== self.ultimaVista) return;
        self.versionVista = version || null;
        if (!self.versionVista) {
          self.verAbierto = false;
          self.$f7.dialog.alert(self.$t('normasvacaciones.errorVersion'));
        }
      }).catch(function(e) {
        if (mia !== self.ultimaVista) return;
        self.verAbierto = false;
        self.tratarError(e);
      });
    },
    cerrarVer: function() {
      // Se invalida la petición en vuelo: su respuesta, buena o mala, es de una ventana que ya no
      // está delante, y la de la apertura siguiente lleva su propio número.
      this.ultimaVista++;
      this.verAbierto = false;
      this.versionVista = null;
      this.numeroVersionVista = null;
    }
  }
};
</script>
